import React, { Suspense, lazy, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FolderOpen } from 'lucide-react';
import { ConfirmModal } from '../../ui/Modal';
import { checkCloneFolder, linkClone, pickFolder } from './localClone';
import { errorText } from './runErrors';
import { isTauri } from '../../../utils/isTauri';

// The clone dialog pulls in the Git page's account and Vault pickers: loaded
// on first use, so the project page stays light (and free of Tauri imports).
const CloneRepoModal = lazy(() => import('./CloneRepoModal'));

/**
 * A repository linked by URL that has no folder on this device yet: offer
 * what VS Code / JetBrains offer — open an existing clone, or clone it here.
 * Either way the folder is remembered on this device only (localClone.js),
 * and `onLinked` lets the Run section show the configurations right away.
 */
export default function LinkLocalClone({ repo, onLinked }) {
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mismatch, setMismatch] = useState(null); // { root, linked, origin }
  const [cloning, setCloning] = useState(false);
  const label = repo.name || repo.remote || repo.host || '?';
  const desktop = isTauri(); // the web app has no local folders

  const link = (root) => {
    try {
      linkClone(repo, root);
      onLinked?.(repo, root);
    } catch {
      setError(t('projects.run.clone.storageRefused'));
    }
  };

  const choose = async () => {
    setError(null);
    let picked = '';
    try {
      picked = await pickFolder();
    } catch (e) {
      setError(String(e?.message || e));
      return;
    }
    if (!picked) return;
    setBusy(true);
    try {
      const res = await checkCloneFolder(repo, picked);
      if (res.status === 'ok') link(res.root);
      else setMismatch(res);
    } catch (e) {
      setError(errorText(t, String(e?.message || e)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pj-run-repo pj-run-unlinked" data-testid="run-unlinked" data-repo-id={repo.id}>
      <div className="pj-run-repo-head">
        <span className="pj-run-repo-name">{label}</span>
        {repo.branch && <span className="pj-run-source">{repo.branch}</span>}
      </div>
      <p className="pj-result-meta pj-run-note">{desktop ? t('projects.run.clone.hint') : t('projects.run.clone.desktopOnly')}</p>
      {desktop && (
      <div className="pj-run-link-actions">
        <button type="button" className="gd-btn" onClick={choose} disabled={busy} data-testid="run-choose-clone">
          <FolderOpen size={13} /> {t('projects.run.clone.choose')}
        </button>
        {repo.remote && (
          <button type="button" className="gd-btn" onClick={() => setCloning(true)} disabled={busy} data-testid="run-clone-here">
            <Download size={13} /> {t('projects.run.clone.cloneHere')}
          </button>
        )}
      </div>
      )}
      {error && (
        <div className="graph-error" role="alert" data-testid="run-clone-error">
          {error}
        </div>
      )}
      <ConfirmModal
        isOpen={!!mismatch}
        onClose={() => setMismatch(null)}
        onConfirm={() => {
          const root = mismatch?.root;
          setMismatch(null);
          if (root) link(root);
        }}
        type="warning"
        title={t('projects.run.clone.mismatchTitle')}
        message={
          <span data-testid="run-clone-mismatch">
            {mismatch?.origin ? t('projects.run.clone.mismatchMessage') : t('projects.run.clone.noOriginMessage')}
            <span className="pj-run-approve-cmd">
              <span className="pj-run-approve-name">{t('projects.run.clone.linkedUrl')}</span>
              <code data-testid="run-clone-mismatch-linked">{mismatch?.linked || '—'}</code>
            </span>
            <span className="pj-run-approve-cmd">
              <span className="pj-run-approve-name">{t('projects.run.clone.folderOrigin')}</span>
              <code data-testid="run-clone-mismatch-origin">{mismatch?.origin || t('projects.run.clone.noOrigin')}</code>
            </span>
            <span className="pj-run-approve-meta">{mismatch?.root}</span>
          </span>
        }
        confirmText={t('projects.run.clone.useAnyway')}
        cancelText={t('common.cancel')}
      />
      {cloning && (
        <Suspense fallback={null}>
          <CloneRepoModal
            repo={repo}
            onClose={() => setCloning(false)}
            onCloned={(res) => {
              setCloning(false);
              link(res.path);
            }}
          />
        </Suspense>
      )}
    </div>
  );
}
